import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { authAPI } from '../services/api'
import { STORAGE_KEYS } from '../constants'

interface User {
  id: string
  email: string
  role: string
  firstLogin: boolean
  profilePicture?: string
  employee?: {
    id: string
    firstName: string
    lastName: string
    phone?: string
    address?: string
    hireDate: string
    salary?: number
    profilePicture?: string
  }
}

interface AuthContextType {
  user: User | null
  token: string | null
  login: (email: string, password: string) => Promise<User>
  logout: () => void
  loading: boolean
  showFirstTimeLogin: boolean
  handlePasswordChanged: () => void
  updateUserProfilePicture: (profilePictureUrl: string | null) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN))
  const [loading, setLoading] = useState(true)
  const [showFirstTimeLogin, setShowFirstTimeLogin] = useState(false)

  // Note: Authorization headers are now handled by the API service interceptors

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const userData = await authAPI.getProfile()
          setUser(userData)
        } catch (error) {
          console.error('Auth check failed:', error)
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
          setToken(null)
        }
      }
      setLoading(false)
    }

    checkAuth()
  }, [token])

  const login = async (email: string, password: string): Promise<User> => {
    try {
      const { token: newToken, user: userData } = await authAPI.login(email, password)
      
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, newToken)
      setToken(newToken)
      setUser(userData)

      // Check if this is a first-time login
      if (userData.firstLogin) {
        setShowFirstTimeLogin(true)
      }

      return userData
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Login failed')
    }
  }

  const logout = () => {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
    setToken(null)
    setUser(null)
  }

  const handlePasswordChanged = () => {
    setShowFirstTimeLogin(false)
    // Refresh user data to update firstLogin status
    if (user) {
      setUser({ ...user, firstLogin: false })
    }
  }

  const updateUserProfilePicture = (profilePictureUrl: string | null) => {
    if (user) {
      setUser({ ...user, profilePicture: profilePictureUrl || undefined })
    }
  }

  const value = {
    user,
    token,
    login,
    logout,
    loading,
    showFirstTimeLogin,
    handlePasswordChanged,
    updateUserProfilePicture
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 